import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { ClassSelect } from "@/components/StudentPicker";
import { DayStrip } from "@/components/DayStrip";
import { AttachmentList } from "@/components/Attachments";
import { todayStr } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ImagePlus, Loader2, CopyCheck } from "lucide-react";

const COLS = [["food_intake", "Food intake", "w-40"], ["nap_time", "Nap time", "w-28"], ["washroom", "Washroom", "w-32"], ["behavior", "Behaviour", "w-40"], ["health_observation", "Health", "w-40"], ["teacher_remarks", "Remarks", "w-56"]];
const emptyRow = () => Object.fromEntries(COLS.map(([k]) => [k, ""]));

export function BulkUpdateForm({ onDone }) {
  const [cls, setCls] = useState({ branch_id: "", class_name: "", section: "" });
  const [date, setDate] = useState(todayStr());
  const [shared, setShared] = useState({ classroom_activities: "", learning_activities: "", photos: [] });
  const [students, setStudents] = useState([]);
  const [rows, setRows] = useState({});
  const [skip, setSkip] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [markedDays, setMarkedDays] = useState([]);
  useEffect(() => {
    if (cls.class_name && cls.section) api.get("/student-updates/dates", { params: { class_name: cls.class_name, section: cls.section, branch_id: cls.branch_id || undefined } }).then((r) => setMarkedDays(r.data)).catch(() => {});
  }, [cls.branch_id, cls.class_name, cls.section, busy]);

  useEffect(() => {
    if (!cls.class_name || !cls.section) { setStudents([]); return; }
    Promise.all([api.get("/class-students", { params: cls }), api.get("/student-updates", { params: { day: date, class_name: cls.class_name, section: cls.section, limit: 100 } })])
      .then(([s, u]) => {
        setStudents(s.data);
        const ex = Object.fromEntries(u.data.items.map((x) => [x.student_id, x]));
        setRows(Object.fromEntries(s.data.map((st) => [st.id, ex[st.id] ? Object.fromEntries(COLS.map(([k]) => [k, ex[st.id][k] || ""])) : emptyRow()])));
        const first = u.data.items[0];
        if (first) setShared((x) => ({ ...x, classroom_activities: first.classroom_activities || "", learning_activities: first.learning_activities || "" }));
        setSkip({});
      }).catch((e) => toast.error(formatApiError(e)));
  }, [cls.branch_id, cls.class_name, cls.section, date]);

  const setCell = (id, k, v) => setRows({ ...rows, [id]: { ...rows[id], [k]: v } });
  const fillDown = (k) => { const v = rows[students[0]?.id]?.[k] || ""; setRows(Object.fromEntries(students.map((s) => [s.id, { ...rows[s.id], [k]: v }]))); };

  async function addPhotos(e) {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    e.target.value = "";
    setUploading(true);
    const out = [];
    for (const f of files) {
      try { const fd = new FormData(); fd.append("file", f); out.push((await api.post("/upload/attachment", fd)).data); }
      catch (err) { toast.error(`${f.name}: ${formatApiError(err)}`); }
    }
    setShared((x) => ({ ...x, photos: [...x.photos, ...out].slice(0, 8) }));
    setUploading(false);
  }

  async function save() {
    setBusy(true);
    try {
      const list = students.filter((s) => !skip[s.id]).map((s) => ({ student_id: s.id, ...rows[s.id] }));
      const { data } = await api.post("/student-updates/bulk", { date, classroom_activities: shared.classroom_activities, learning_activities: shared.learning_activities, photos: shared.photos, rows: list });
      toast.success(`Saved: ${data.created} new, ${data.updated} updated${data.skipped ? `, ${data.skipped} skipped` : ""}`);
      setShared((x) => ({ ...x, photos: [] }));
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  }

  return (
    <div data-testid="bulk-form" className="bg-white rounded-2xl shadow-sm p-5 mb-6 space-y-4">
      <h3 className="font-display font-bold text-slate-900">Whole-class daily update</h3>
      <div className="flex flex-wrap gap-4 items-end">
        <ClassSelect value={cls} onChange={setCls} />
      </div>
      <div><Label>Date</Label><div className="mt-1"><DayStrip testid="bulk-date" marked={markedDays} countLabel="updates"
        preview={(d) => api.get("/student-updates", { params: { day: d, class_name: cls.class_name, section: cls.section, limit: 100 } }).then((r) => r.data.items.map((x) => ({ id: x.id, title: x.student_name, sub: `by ${x.teacher_name}` })))} max={todayStr()} value={date} onChange={(v) => v && setDate(v)} /></div></div>
      <div className="grid md:grid-cols-2 gap-3">
        <div><Label>Classroom activities (whole class)</Label><Textarea data-testid="bulk-classroom" rows={2} value={shared.classroom_activities} onChange={(e) => setShared({ ...shared, classroom_activities: e.target.value })} className="mt-1" /></div>
        <div><Label>Learning activities (whole class)</Label><Textarea data-testid="bulk-learning" rows={2} value={shared.learning_activities} onChange={(e) => setShared({ ...shared, learning_activities: e.target.value })} className="mt-1" /></div>
      </div>
      <div className="space-y-2">
        <label className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 border border-violet-200 rounded-lg px-3 py-1.5 cursor-pointer hover:bg-violet-50">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} Class photos (added to every student's update)
          <input data-testid="bulk-photos-input" type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={addPhotos} />
        </label>
        <AttachmentList items={shared.photos} onRemove={(i) => setShared({ ...shared, photos: shared.photos.filter((_, j) => j !== i) })} />
      </div>
      {students.length === 0 ? <p className="text-sm text-slate-500">{cls.class_name ? "No students in this class." : "Choose a class to load students."}</p> : (
        <div className="overflow-x-auto border border-violet-100 rounded-xl">
          <table className="text-sm min-w-full">
            <thead className="bg-violet-50">
              <tr>
                <th className="text-left px-3 py-2 sticky left-0 bg-violet-50">Student</th>
                {COLS.map(([k, l]) => (
                  <th key={k} className="text-left px-2 py-2 whitespace-nowrap">{l}
                    <button type="button" title="Copy first row to all" data-testid={`bulk-fill-${k}`} onClick={() => fillDown(k)} className="ml-1 text-violet-500 hover:text-violet-800 align-middle"><CopyCheck size={13} /></button></th>
                ))}
                <th className="px-2">Skip</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {students.map((s) => (
                <tr key={s.id} data-testid={`bulk-row-${s.id}`} className={skip[s.id] ? "opacity-40" : ""}>
                  <td className="px-3 py-1.5 font-semibold whitespace-nowrap sticky left-0 bg-white">{s.name}</td>
                  {COLS.map(([k, , w]) => <td key={k} className="px-1.5 py-1.5"><Input data-testid={`bulk-${k}-${s.id}`} value={rows[s.id]?.[k] || ""} disabled={skip[s.id]} onChange={(e) => setCell(s.id, k, e.target.value)} className={`h-9 ${w}`} /></td>)}
                  <td className="text-center"><Checkbox data-testid={`bulk-skip-${s.id}`} checked={!!skip[s.id]} onCheckedChange={(v) => setSkip({ ...skip, [s.id]: !!v })} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Button data-testid="bulk-save" disabled={busy || !students.length} onClick={save} className="btn-brand">{busy ? "Saving…" : `Save for ${students.filter((s) => !skip[s.id]).length} students`}</Button>
    </div>
  );
}
